import { useState, useEffect, useRef } from "react";
import { ChevronRight, ChevronLeft, ArrowUpRight, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function OeiaWebsite() {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Animated DEEP SEA background with moving light blue/purple glow
  const timeRef = useRef(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight * 2;
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    // Create floating particles
    for (let i = 0; i < 120; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.5 + 0.2
      });
    }
    
    const animate = () => {
      timeRef.current += 0.008;
      const time = timeRef.current;
      
      // Dark base
      ctx.fillStyle = '#020817';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // ANIMATED moving glow - LIGHT BLUE with PURPLE tint (موف)
      // Main center glow that moves
      const centerX = canvas.width / 2 + Math.sin(time) * 150;
      const centerY = canvas.height * 0.15 + Math.cos(time * 0.7) * 50;
      
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, canvas.width * 0.8
      );
      // Light blue with purple/violet tint
      gradient.addColorStop(0, 'rgba(120, 140, 255, 0.6)');
      gradient.addColorStop(0.2, 'rgba(100, 120, 255, 0.4)');
      gradient.addColorStop(0.4, 'rgba(80, 100, 220, 0.25)');
      gradient.addColorStop(0.7, 'rgba(60, 80, 180, 0.1)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Secondary moving glow - left side
      const leftX = canvas.width * 0.2 + Math.sin(time * 1.2) * 100;
      const leftY = canvas.height * 0.25 + Math.cos(time * 0.8) * 80;
      const gradient2 = ctx.createRadialGradient(leftX, leftY, 0, leftX, leftY, canvas.width * 0.5);
      gradient2.addColorStop(0, 'rgba(130, 100, 255, 0.3)');
      gradient2.addColorStop(0.5, 'rgba(100, 80, 220, 0.15)');
      gradient2.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient2;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Third moving glow - right side
      const rightX = canvas.width * 0.8 + Math.cos(time * 0.9) * 100;
      const rightY = canvas.height * 0.2 + Math.sin(time * 1.1) * 60;
      const gradient3 = ctx.createRadialGradient(rightX, rightY, 0, rightX, rightY, canvas.width * 0.5);
      gradient3.addColorStop(0, 'rgba(100, 150, 255, 0.3)');
      gradient3.addColorStop(0.5, 'rgba(80, 120, 220, 0.15)');
      gradient3.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient3;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw particles - light blue/purple
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        const particleHue = 220 + Math.sin(time + p.x * 0.01) * 30;
        ctx.fillStyle = `hsla(${particleHue}, 80%, 75%, ${p.opacity})`;
        ctx.fill();
        
        p.x += p.speedX;
        p.y += p.speedY;
        
        if (p.x < 0 || p.x > canvas.width) p.speedX *= -1;
        if (p.y < 0 || p.y > canvas.height) p.speedY *= -1;
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div 
      className="min-h-screen text-white relative overflow-x-hidden" 
      style={{ 
        background: '#000',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }} 
      dir="rtl"
      data-testid="oeia-website"
    >
      {/* Animated Canvas Background */}
      <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10">
        
        {/* Navigation - FIXED, WHITE text for logo & CTA, LIGHT BLUE for rest */}
        <nav 
          className="fixed top-0 left-0 right-0 z-50 px-4 md:px-8 py-4"
          style={{ 
            background: 'rgba(2,8,23,0.8)', 
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
          }}
          data-testid="website-nav"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo - WHITE */}
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  boxShadow: '0 0 30px rgba(99,102,241,0.5)'
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
              </div>
              <span className="text-xl font-bold tracking-tight text-white">oeia.io</span>
            </div>

            {/* Desktop Nav Links - LIGHT BLUE */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-[#93C5FD] hover:text-white transition-colors text-[15px]">المميزات</a>
              <a href="#pricing" className="text-[#93C5FD] hover:text-white transition-colors text-[15px]">الأسعار</a>
              <a href="#faq" className="text-[#93C5FD] hover:text-white transition-colors text-[15px]">الأسئلة الشائعة</a>
              <button 
                onClick={handleLogin}
                className="text-[#93C5FD] hover:text-white transition-colors text-[15px]"
                data-testid="nav-login-btn"
              >
                تسجيل الدخول
              </button>
            </div>

            {/* CTA Button - WHITE text */}
            <button 
              onClick={handleGetStarted}
              className="hidden md:flex px-6 py-2.5 rounded-[10px] font-medium text-[15px] text-white items-center gap-2 transition-all duration-300 hover:scale-105 active:scale-95"
              style={{ 
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                boxShadow: '0px 8px 40px rgba(99, 102, 241, 0.5), 0px 0px 0px 1px rgba(99, 102, 241, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.15)'
              }}
              data-testid="nav-cta-btn"
            >
              ابدأ الآن
              <ChevronLeft size={18} />
            </button>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div 
              className="md:hidden absolute top-full left-0 right-0 p-4"
              style={{ 
                background: 'rgba(2,8,23,0.98)',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-[#93C5FD] py-2">المميزات</a>
                <a href="#pricing" className="text-[#93C5FD] py-2">الأسعار</a>
                <a href="#faq" className="text-[#93C5FD] py-2">الأسئلة الشائعة</a>
                <button onClick={handleLogin} className="text-[#93C5FD] py-2 text-right">تسجيل الدخول</button>
                <button 
                  onClick={handleGetStarted}
                  className="w-full py-3 rounded-[10px] font-medium text-white mt-2"
                  style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' }}
                >
                  ابدأ الآن
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section - WHITE text, light blue/purple theme */}
        <section className="min-h-screen flex flex-col items-center justify-center px-4 md:px-8 pt-24 pb-16">
          
          {/* Badge/Tag */}
          <a 
            href="#pricing" 
            className="flex items-center gap-3 px-5 py-3 rounded-full mb-8 transition-all duration-300 hover:scale-105"
            style={{ 
              background: 'rgba(255,255,255,0.08)', 
              border: '1px solid rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)'
            }}
            data-testid="hero-badge"
          >
            {/* Avatar Stack */}
            <div className="flex -space-x-3 space-x-reverse">
              <img src="https://randomuser.me/api/portraits/women/44.jpg" className="w-8 h-8 rounded-full border-2 border-black object-cover" alt="" />
              <img src="https://randomuser.me/api/portraits/men/32.jpg" className="w-8 h-8 rounded-full border-2 border-black object-cover" alt="" />
              <img src="https://randomuser.me/api/portraits/women/68.jpg" className="w-8 h-8 rounded-full border-2 border-black object-cover" alt="" />
            </div>
            <span className="text-white text-[15px] font-medium">انضم لنادي جمع العملاء المحتملين™</span>
          </a>

          {/* Main Headline - WHITE text */}
          <h1 
            className="text-center font-bold leading-[1.1] mb-6 max-w-5xl text-white"
            style={{ fontSize: 'clamp(2.8rem, 7vw, 5rem)' }}
            data-testid="hero-headline"
          >
            <span className="inline-block ml-2">🚀</span>
            اجمع +1,000 عميل محتمل في دقائق.
            <br />
            <span 
              style={{ 
                background: 'linear-gradient(135deg, #818CF8 0%, #A78BFA 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              مضمون.
            </span>
          </h1>

          {/* Subheadline - WHITE */}
          <p 
            className="text-center mb-10 max-w-2xl text-white"
            style={{ 
              fontSize: 'clamp(1.1rem, 2.2vw, 1.35rem)',
              lineHeight: '1.6'
            }}
            data-testid="hero-subheadline"
          >
            اجمع عملاء B2B و B2C من <span className="font-semibold text-[#C4B5FD]">9 مصادر مختلفة</span> في دقائق معدودة.
          </p>

          {/* CTA Button */}
          <button 
            onClick={handleGetStarted}
            className="px-10 py-4 rounded-[10px] font-semibold text-lg text-white flex items-center gap-2 transition-all duration-300 hover:scale-105 active:scale-95 mb-16"
            style={{ 
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              boxShadow: '0px 8px 50px rgba(99, 102, 241, 0.6), inset 0px 1px 0px rgba(255,255,255,0.1), 0px 0px 0px 1px rgba(99, 102, 241, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            data-testid="hero-cta-btn"
          >
            ابدأ الآن!
          </button>

          {/* Dashboard Preview Card */}
          <div className="w-full max-w-5xl relative">
            {/* Glow Effect Behind Card - Light blue/purple */}
            <div 
              className="absolute -inset-16 z-0"
              style={{ 
                background: 'radial-gradient(ellipse at center top, rgba(129,140,248,0.4) 0%, rgba(139,92,246,0.2) 40%, transparent 70%)',
                filter: 'blur(50px)'
              }}
            />
            
            {/* Dashboard Card */}
            <div 
              className="relative z-10 rounded-2xl overflow-hidden"
              style={{ 
                background: 'linear-gradient(180deg, rgba(15,20,40,0.95) 0%, rgba(10,15,30,0.98) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 30px 100px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05)'
              }}
              data-testid="dashboard-preview"
            >
              {/* Card Header */}
              <div 
                className="flex items-center justify-between px-6 py-4"
                style={{ 
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(0,0,0,0.3)'
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ 
                        background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                        boxShadow: '0 4px 15px rgba(99,102,241,0.4)'
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold text-white block">Lead Generator</span>
                      <span className="text-xs text-[#93C5FD]">Multi-platform lead scraper</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span 
                    className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5"
                    style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' }}
                  >
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                    Active
                  </span>
                  <div className="hidden md:flex items-center gap-1 bg-white/5 rounded-lg p-1">
                    {['Lists', 'Campaigns', 'CRM', 'Settings', 'Profile'].map((tab, i) => (
                      <span 
                        key={tab}
                        className={`text-xs px-3 py-1.5 rounded-md transition-colors ${i === 0 ? 'bg-white/10 text-white' : 'text-[#93C5FD] hover:text-white'}`}
                      >
                        {tab}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Card Content */}
              <div className="p-10 md:p-14">
                <p 
                  className="text-2xl md:text-3xl font-light text-center mb-10 text-white"
                  style={{ fontStyle: 'italic', opacity: 0.6 }}
                >
                  Who is your next customer?
                </p>
                
                {/* Search Input Mock */}
                <div 
                  className="max-w-2xl mx-auto rounded-xl p-4 flex items-center gap-4"
                  style={{ 
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(147,197,253,0.5)" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <span className="text-[#93C5FD]" style={{ opacity: 0.6 }}>Search leads by location, industry, or keywords...</span>
                </div>
              </div>
              
              {/* Animated Line - Light purple */}
              <div 
                className="h-[3px] w-full relative overflow-hidden"
                style={{ background: 'rgba(139,92,246,0.2)' }}
              >
                <div 
                  className="absolute inset-0"
                  style={{ 
                    background: 'linear-gradient(90deg, transparent, #818CF8, #A78BFA, transparent)',
                    animation: 'lineMove 2.5s ease-in-out infinite'
                  }}
                />
              </div>
            </div>
            
            {/* Bottom Glow Lights - Light blue/purple */}
            <div 
              className="absolute -bottom-12 left-1/4 w-60 h-60 rounded-full"
              style={{ 
                background: 'radial-gradient(circle, rgba(129,140,248,0.5) 0%, transparent 70%)',
                filter: 'blur(40px)'
              }}
            />
            <div 
              className="absolute -bottom-12 right-1/4 w-60 h-60 rounded-full"
              style={{ 
                background: 'radial-gradient(circle, rgba(139,92,246,0.5) 0%, transparent 70%)',
                filter: 'blur(40px)'
              }}
            />
            <div 
              className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full"
              style={{ 
                background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                filter: 'blur(30px)'
              }}
            />
          </div>
        </section>

        {/* Features Section - About */}
        <section id="features" className="py-24 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Section Badge */}
            <div className="flex justify-center mb-6">
              <div 
                className="flex items-center gap-4 px-4 py-2"
              >
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(196, 216, 255, 0.5))' }} />
                <span className="text-sm font-medium" style={{ color: 'rgba(196, 216, 255, 0.8)' }}>حول oeia.io</span>
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, rgba(196, 216, 255, 0.5), transparent)' }} />
              </div>
            </div>

            {/* Section Title */}
            <h2 
              className="text-center font-bold mb-6"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              اجمع عملاء بلا حدود وأرسل رسائل تسويقية
              <br />
              <span style={{ color: '#0055FE' }}>بدون أي قيود!</span>
            </h2>

            <p 
              className="text-center max-w-2xl mx-auto mb-16"
              style={{ color: 'rgba(255,255,255,0.6)' }}
            >
              مع oeia.io، يمكنك جمع العملاء المحتملين وإرسال رسائل تسويقية مخصصة بدون أي قيود.
            </p>

            {/* Feature Cards - Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Feature 1 - Large */}
              <div 
                className="rounded-2xl p-8 relative overflow-hidden"
                style={{ 
                  background: 'linear-gradient(180deg, rgba(15,15,25,0.9) 0%, rgba(5,5,15,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <h3 className="text-xl font-bold mb-3">+8 مصادر لجمع العملاء</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>اجمع العملاء من أكثر من 8 منصات مختلفة.</p>
                
                {/* Platform logos carousel placeholder */}
                <div className="flex gap-3 mt-6 overflow-hidden">
                  {['📱', '💬', '📘', '📸', '🎵', '▶️', '💼', '📍'].map((icon, i) => (
                    <div 
                      key={i}
                      className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                      style={{ 
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                    >
                      {icon}
                    </div>
                  ))}
                </div>
              </div>

              {/* Feature 2 */}
              <div 
                className="rounded-2xl p-8 relative overflow-hidden"
                style={{ 
                  background: 'linear-gradient(180deg, rgba(15,15,25,0.9) 0%, rgba(5,5,15,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <h3 className="text-xl font-bold mb-3">أتمتة التواصل</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                  تواصل مع آلاف العملاء المحتملين تلقائياً، بدون قيود إرسال أو عمل يدوي.
                </p>
                
                <div className="flex items-center justify-center mt-6">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(135deg, #0055FE 0%, #3B82F6 100%)',
                      boxShadow: '0 0 40px rgba(0,85,254,0.4)'
                    }}
                  >
                    <span className="text-3xl">📧</span>
                  </div>
                </div>
              </div>

              {/* Feature 3 */}
              <div 
                className="rounded-2xl p-8 relative overflow-hidden"
                style={{ 
                  background: 'linear-gradient(180deg, rgba(15,15,25,0.9) 0%, rgba(5,5,15,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <h3 className="text-xl font-bold mb-3">تحليلات متقدمة</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                  تابع أداء حملاتك التسويقية مع تقارير وإحصائيات مفصلة.
                </p>
                
                <div className="flex items-center justify-center mt-6">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      boxShadow: '0 0 40px rgba(16,185,129,0.3)'
                    }}
                  >
                    <span className="text-3xl">📊</span>
                  </div>
                </div>
              </div>

              {/* Feature 4 */}
              <div 
                className="rounded-2xl p-8 relative overflow-hidden"
                style={{ 
                  background: 'linear-gradient(180deg, rgba(15,15,25,0.9) 0%, rgba(5,5,15,0.95) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <h3 className="text-xl font-bold mb-3">تصدير سهل</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                  صدّر بياناتك بسهولة إلى ملفات CSV أو Excel للاستخدام في أدواتك المفضلة.
                </p>
                
                <div className="flex items-center justify-center mt-6">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                      boxShadow: '0 0 40px rgba(139,92,246,0.3)'
                    }}
                  >
                    <span className="text-3xl">📥</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Section - Why Choose Us */}
        <section className="py-24 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Section Badge */}
            <div className="flex justify-center mb-6">
              <div className="flex items-center gap-4 px-4 py-2">
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(196, 216, 255, 0.5))' }} />
                <span className="text-sm font-medium" style={{ color: 'rgba(196, 216, 255, 0.8)' }}>لماذا تختارنا</span>
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, rgba(196, 216, 255, 0.5), transparent)' }} />
              </div>
            </div>

            <h2 
              className="text-center font-bold mb-16"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              اختر oeia.io عن غيرها
            </h2>

            {/* Comparison Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Others Card */}
              <div 
                className="rounded-2xl p-6 relative"
                style={{ 
                  background: 'radial-gradient(96% 96% at 48.8% -35.7%, rgba(138, 165, 255, 0.1) 0%, rgb(0, 0, 0) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <h3 className="text-center text-lg font-semibold mb-6" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  أدوات جمع العملاء الأخرى
                </h3>
                
                <div 
                  className="rounded-xl p-4"
                  style={{ 
                    background: 'radial-gradient(96% 96% at 36.1% 27.7%, rgba(10, 12, 30, 0.9) 0%, rgb(0, 0, 0) 100%)'
                  }}
                >
                  {['إعداد بطيء', 'تكاليف عالية', 'عمليات ثابتة', 'تأخير في الردود', 'شفافية منخفضة'].map((item, i) => (
                    <div key={i}>
                      <div className="flex items-center gap-3 py-3">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                          <X size={12} className="text-red-400" />
                        </div>
                        <span style={{ color: 'rgba(205, 211, 233, 0.9)' }}>{item}</span>
                      </div>
                      {i < 4 && <div className="h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }} />}
                    </div>
                  ))}
                </div>
              </div>

              {/* oeia.io Card */}
              <div 
                className="rounded-2xl p-6 relative"
                style={{ 
                  background: 'radial-gradient(96% 96% at 48.8% -35.7%, rgba(0, 85, 254, 0.2) 0%, rgb(0, 0, 0) 100%)',
                  border: '1px solid rgba(0,85,254,0.3)'
                }}
              >
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: '#0055FE' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold">oeia.io</h3>
                </div>
                
                <div 
                  className="rounded-xl p-4"
                  style={{ 
                    background: 'radial-gradient(96% 96% at 36.1% 27.7%, rgba(10, 12, 30, 0.9) 0%, rgb(0, 0, 0) 100%)'
                  }}
                >
                  {['إعداد فوري', 'خطط بأسعار معقولة', 'سير عمل مرن', 'تحديثات فورية', 'شفافية كاملة'].map((item, i) => (
                    <div key={i}>
                      <div className="flex items-center gap-3 py-3">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                          <svg width="12" height="12" viewBox="0 0 12 9" fill="none">
                            <path d="M1.286 4.725L4.286 7.875L10.714 1.125" stroke="#22c55e" strokeWidth="1.71" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ color: 'rgba(205, 211, 233, 0.9)' }}>{item}</span>
                      </div>
                      {i < 4 && <div className="h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)' }} />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-24 px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Section Badge */}
            <div className="flex justify-center mb-6">
              <div className="flex items-center gap-4 px-4 py-2">
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(196, 216, 255, 0.5))' }} />
                <span className="text-sm font-medium" style={{ color: 'rgba(196, 216, 255, 0.8)' }}>الأسئلة الشائعة</span>
                <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, rgba(196, 216, 255, 0.5), transparent)' }} />
              </div>
            </div>

            <h2 
              className="text-center font-bold mb-6"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
            >
              لديك أسئلة؟ لدينا إجابات
            </h2>

            <p 
              className="text-center max-w-2xl mx-auto mb-12"
              style={{ color: 'rgba(255,255,255,0.6)' }}
            >
              إليك بعض الأسئلة الشائعة. إذا لم تجد إجابتك، تواصل معنا مباشرة!
            </p>

            {/* FAQ Items */}
            <div className="space-y-4">
              {[
                { q: 'ما هو oeia.io؟', a: 'oeia.io هي منصة شاملة لجمع العملاء المحتملين من منصات التواصل الاجتماعي المختلفة وإرسال رسائل تسويقية مخصصة.' },
                { q: 'كيف يساعد oeia.io عملي على النمو؟', a: 'من خلال جمع بيانات العملاء المحتملين من 8+ منصات وأتمتة التواصل معهم، مما يوفر وقتك ويزيد من فرص التحويل.' },
                { q: 'هل يمكنني دمج oeia.io مع أدوات أخرى؟', a: 'نعم! يمكنك تصدير البيانات بسهولة إلى CSV أو Excel للاستخدام في CRM المفضل لديك.' },
                { q: 'هل بياناتي آمنة مع oeia.io؟', a: 'نعم، نستخدم أحدث معايير الأمان لحماية بياناتك وبيانات عملائك.' },
              ].map((item, i) => (
                <details 
                  key={i}
                  className="group rounded-xl"
                  style={{ 
                    background: 'rgba(15,15,25,0.5)',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="font-medium">{item.q}</span>
                    <ChevronRight size={18} className="transform group-open:rotate-90 transition-transform" style={{ color: 'rgba(255,255,255,0.5)' }} />
                  </summary>
                  <div className="px-5 pb-5">
                    <p style={{ color: 'rgba(255,255,255,0.6)' }}>{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div 
              className="rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
              style={{ 
                background: 'linear-gradient(180deg, rgba(15,15,25,0.95) 0%, rgba(5,5,15,0.98) 100%)',
                border: '1px solid rgba(0,85,254,0.3)'
              }}
            >
              {/* Background glow */}
              <div 
                className="absolute inset-0 opacity-30"
                style={{ 
                  background: 'radial-gradient(ellipse at top center, rgba(0,85,254,0.4) 0%, transparent 60%)'
                }}
              />
              
              <div className="relative z-10">
                {/* Badge */}
                <div className="flex justify-center mb-6">
                  <div className="flex items-center gap-4 px-4 py-2">
                    <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(196, 216, 255, 0.5))' }} />
                    <span className="text-sm font-medium" style={{ color: 'rgba(196, 216, 255, 0.8)' }}>اجمع العملاء تلقائياً</span>
                    <div className="h-[1px] w-16" style={{ background: 'linear-gradient(90deg, rgba(196, 216, 255, 0.5), transparent)' }} />
                  </div>
                </div>

                <h2 
                  className="font-bold mb-6"
                  style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)' }}
                >
                  هل أنت مستعد لتنمية عملك مع التواصل الآلي؟
                </h2>

                <p 
                  className="max-w-xl mx-auto mb-8"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  ابدأ استخدام oeia.io اليوم واجمع عملاء محتملين + أرسل رسائل تسويقية مخصصة — كل ذلك بشكل آلي.
                </p>

                <button 
                  onClick={handleGetStarted}
                  className="px-8 py-4 rounded-[10px] font-semibold text-white inline-flex items-center gap-2 transition-all duration-300 hover:scale-105 active:scale-95"
                  style={{ 
                    background: '#0055FE',
                    boxShadow: '0px 8px 40px rgba(0, 85, 255, 0.5), 0px 0px 0px 1px rgba(0, 85, 255, 0.12)',
                    border: '1px solid rgba(255, 255, 255, 0.15)'
                  }}
                  data-testid="cta-btn"
                >
                  ابدأ الآن
                  <ArrowUpRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer 
          className="py-8 px-4 md:px-8 text-center"
          style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: '#0055FE' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <span className="font-bold">oeia.io</span>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.4)' }}>© 2025 oeia.io. جميع الحقوق محفوظة.</p>
        </footer>

      </div>

      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        @keyframes lineMove {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        details > summary::-webkit-details-marker {
          display: none;
        }
      `}</style>
    </div>
  );
}
